import MobileMonthNavigation from "./MobileMonthNavigation";
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import './Home.css';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import useAppointments from "../hooks/useAppointments";
import useCalendar from "../hooks/useCalendar";
import useHighlight from "../hooks/useHighlight";
import useReschedule from "../hooks/useReschedule";
import useAppointmentModal from "../hooks/useAppointmentModal";
import useAppointmentForm from "../hooks/useAppointmentForm";
import AppointmentImageViewer from "./AppointmentImageViewer";
import AppointmentCard from "./AppointmentCard";
import DayCard from "./DayCard";
import MonthSection from "./MonthSection";
import MobileCalendarSidebar from "./MobileCalendarSidebar";
import CalendarToolbar from "./CalendarToolbar";
import AddAppointmentModal from "./AddAppointmentModal";
import EditAppointmentModal from "./EditAppointmentModal";
import TattooProjectDialog from "./TattooProjectDialog";
import RescheduleModal from "./RescheduleModal";
import CustomerSearchDialog from './CustomerSearchDialog';
import './RetouchAppointment.css';
import './RetouchCalendar.css';
import RetouchCalendarOptions from './RetouchCalendarOptions';
import { filterRetouchCalendar } from '../hooks/filterRetouchCalendar';
import CalendarLoadingState from './CalendarLoadingState';
import './CalendarLoadingState.css';
import BookingDiagnostics from './BookingDiagnostics';
import axios from 'axios';
import useCalendarArtistProfiles from '../hooks/useCalendarArtistProfiles';
import './CalendarArtistProfiles.css';


// NRW public holidays. Gregorian Easter; UTC arithmetic avoids DST shifts.
// Source: Feiertagsgesetz NRW, section 2.
function getNRWHolidays(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  const easter = Date.UTC(year, month - 1, day);
  const fromEaster = offset =>
    new Date(easter + offset * 86400000).toISOString().slice(0, 10);

  const holidays = {};
  const entries = [
    [`${year}-01-01`, 'Neujahr'],
    [fromEaster(-2), 'Karfreitag'],
    [fromEaster(1), 'Ostermontag'],
    [`${year}-05-01`, 'Tag der Arbeit'],
    [fromEaster(39), 'Christi Himmelfahrt'],
    [fromEaster(50), 'Pfingstmontag'],
    [fromEaster(60), 'Fronleichnam'],
    [`${year}-10-03`, 'Tag der Deutschen Einheit'],
    [`${year}-11-01`, 'Allerheiligen'],
    [`${year}-12-25`, '1. Weihnachtstag'],
    [`${year}-12-26`, '2. Weihnachtstag'],
  ];
  entries.forEach(([date, name]) => {
    holidays[date] = holidays[date] ? `${holidays[date]} / ${name}` : name;
  });
  return holidays;
}


const Home = ({
  locations,
  selectedLocationId,
  setSelectedLocationId,

}) => {
  const [projectAppointment, setProjectAppointment] = useState(null);
  const openProject = appointment => { closeModal(); setProjectAppointment(appointment); };
  const artistProfiles = useCalendarArtistProfiles(selectedLocationId);
  const bookingRenderCount = useRef(0);
  bookingRenderCount.current += 1;
  const location = useLocation();
  const navigate = useNavigate();
  const handledNavigationHighlight = useRef(false);
  const handledRebookingNavigation = useRef(false);
  // State Variables

  // type: "new" | "edit"
  const [openAppointmentActionsId, setOpenAppointmentActionsId] = useState(null);
  const didAutoScrollToMonth = useRef(false);
  
  const latestLocationRequestRef = useRef(null);
  const [, setIsEditOperation] = useState(false);
  const [isOperationInProgress, setIsOperationInProgress] = useState(false); // Tracks if an operation is ongoing for the scrolling to current month to be controlled
  const [availableDays, setAvailableDays] = useState([]);
  const [groupedDays, setGroupedDays] = useState({});
  // State für Künstler, Kunden und Suchbegriff
  const [artistSearch, setArtistSearch] = useState('');
  const [retouchDraft, setRetouchDraft] = useState(null);
  const [pendingRetouch, setPendingRetouch] = useState(null);
  const [retouchWeekdays, setRetouchWeekdays] = useState([]);
  useEffect(() => { setRetouchDraft(null); }, [selectedLocationId]);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [mobileCustomerQuery, setMobileCustomerQuery] = useState('');

  // primeink-customer-deeplink
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const phone = params.get('customerPhone');

    if (!phone) return;

    const normalized = phone.replace(/[^+0-9]/g, '');

    if (normalized.length < 7 || normalized.length > 20) return;

    setMobileCustomerQuery(normalized);
    setCustomerSearchOpen(true);

    params.delete('customerPhone');

    const remaining = params.toString();
    const cleanUrl =
      window.location.pathname +
      (remaining ? '?' + remaining : '') +
      window.location.hash;

    window.history.replaceState(
      window.history.state,
      '',
      cleanUrl
    );
  }, []);

  useEffect(() => {
    const openSearch = event => { setMobileCustomerQuery(event.detail?.query || ''); setCustomerSearchOpen(true); };
    window.addEventListener('primeink:customer-search', openSearch);
    const pending = sessionStorage.getItem('primeink:customer-search');
    if (pending !== null) { sessionStorage.removeItem('primeink:customer-search'); setMobileCustomerQuery(pending); setCustomerSearchOpen(true); }
    return () => window.removeEventListener('primeink:customer-search', openSearch);
  }, []);
  const previousSearchLocationRef = useRef(selectedLocationId);
  useEffect(() => {
    if (previousSearchLocationRef.current !== selectedLocationId) {
      previousSearchLocationRef.current = selectedLocationId;
      setCustomerSearchOpen(false);
    }
  }, [selectedLocationId]);

  // =======================================================
  // Appointment API Hook
  // -------------------------------------------------------
  // Centralizes backend communication for appointment and
  // calendar data.
  // =======================================================

  const {
    toggleAppointmentConfirmation: toggleConfirmationApi,
    fetchAvailableDaysAndBookingStatus: fetchCalendarData,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    rescheduleAppointment,
  } = useAppointments();

  // =======================================================
  // Calendar Transformation Hook
  // -------------------------------------------------------
  // Provides grouping and filtering transformations for
  // calendar data.
  // =======================================================

  const {
    groupAvailableDays,
    filterGroupedDays,
    buildMobileSidebar,
  } = useCalendar();

  // =======================================================
  // Highlight Hook
  // -------------------------------------------------------
  // Manages pending highlight targets and retry state.
  // Visual DOM effects remain inside Home.js for now.
  // =======================================================

  const {
    pendingHighlightRef,
    highlightTriesRef,
    scrollToTarget,
    setScrollToTarget,
    setPendingHighlight,
  } = useHighlight();

  // =======================================================
  // Reschedule Hook
  // -------------------------------------------------------
  // Manages appointment rescheduling state and modal flow.
  // API execution remains inside Home.js for now.
  // =======================================================

  const {
    rescheduleMode,
    appointmentToMove,
    rescheduleModalIsOpen,
    targetDay,
    rescheduleTime,
    setRescheduleTime,
    startRescheduleMode,
    cancelRescheduleMode,
    openRescheduleModal,
  } = useReschedule();

  // =======================================================
  // Appointment Modal Hook
  // -------------------------------------------------------
  // Manages modal visibility and selected appointment state.
  // Form values remain inside Home.js for now.
  // =======================================================

  const {
    addModalIsOpen,
    editModalIsOpen,
    selectedAppointment,
    isDeleteHover,
    setIsDeleteHover,
    openAddModal,
    openEditModalState,
    closeAppointmentModals,
  } = useAppointmentModal();

  // =======================================================
  // Appointment Form Hook
  // -------------------------------------------------------
  // Manages appointment form values and form preparation.
  // =======================================================

  const {
    newAppointmentData,
    setNewAppointmentData,
    resetNewAppointmentData,
    prepareNewAppointment,
    prepareAppointmentEdit,
  } = useAppointmentForm();

  const [viewMode] = useState('grid'); // 'grid' oder 'list'
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const todayDayRef = useRef(null); // Ref für den heutigen Tag
  const [loading, setLoading] = useState(true);
  const [calendarDataLocationId, setCalendarDataLocationId] = useState(null);
  const [groupedLocationId, setGroupedLocationId] = useState(null);
  const [calendarLoadError, setCalendarLoadError] = useState('');
  const calendarBusy = loading || String(groupedLocationId) !== String(selectedLocationId);

  const [allowAutoScroll, setAllowAutoScroll] = useState(true);
  const holidaysNRW = useMemo(() => {
    const currentYear = moment().year();
    const years = new Set([currentYear, currentYear + 1]);
    availableDays.forEach(day => {
      const date = moment(day.day);
      if (date.isValid()) years.add(date.year());
    });
    const holidays = {};
    years.forEach(year => Object.assign(holidays, getNRWHolidays(year)));
    return holidays;
  }, [availableDays]);
  const [viewerImages, setViewerImages] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [sidebarMonth, setSidebarMonth] = useState(moment().format('YYYY-MM'));
  const [monthNavigationVersion, setMonthNavigationVersion] = useState(0);
  const [pendingSidebarDay, setPendingSidebarDay] = useState(null);
  const sidebarDayNavigationRef = useRef(0);
  const pendingMonthNavigationRef = useRef(null);
  const pendingMonthNavigationTimeoutRef = useRef(null);

  const [isMobileCalendar, setIsMobileCalendar] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateMobileCalendar = () =>
      setIsMobileCalendar(window.innerWidth <= 768);

    window.addEventListener("resize", updateMobileCalendar);
    return () => window.removeEventListener("resize", updateMobileCalendar);
  }, []);

  const [activeSidebarArtist, setActiveSidebarArtist] =
    useState(null);
  const [activeSidebarDayId, setActiveSidebarDayId] =
    useState(null);

  // NEU: Copy-Paste Logik
  const [copiedAppointment, setCopiedAppointment] = useState(null);

  const shareImage = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      const file = new File([blob], 'tattoo-reference.jpg', {
        type: blob.type || 'image/jpeg',
      });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Tattoo Referenzbild',
        });
        return;
      }

      window.open(url, '_blank');
    } catch (err) {
      console.error('Bild konnte nicht geteilt werden:', err);
      window.open(url, '_blank');
    }
  };

  const TATTOO_CATEGORIES = [
    "Realistic",
    "Fineline",
    "Lettering",
    "Anime",
    "Mandala",
    "Tribal",
    "Cover-Up",
    "Color",
    "Sonstige",
  ];



  // =======================================================
  // Fetch Available Days and Booking Status
  // -------------------------------------------------------
  // Loads raw calendar data from the appointment hook,
  // merges each day with its booking status and updates
  // the calendar state for the currently selected location.
  // =======================================================

  const fetchAvailableDaysAndBookingStatus = useCallback(async (
    scrollToDayId = null,
    enableScroll = true,
    locationIdOverride = null
  ) => {
    const requestLocationId =
      locationIdOverride ?? selectedLocationId;

    const requestId = `${requestLocationId}-${Date.now()}-${Math.random()}`;

    latestLocationRequestRef.current = requestId;

    try {
      // =======================================================
      // Load Calendar Data
      // -------------------------------------------------------
      // Retrieves available days and booking status in parallel
      // through the centralized appointment API hook.
      // =======================================================

      const result =
        await fetchCalendarData(requestLocationId);

      if (!result) {
        return;
      }

      const {
        availableDays,
        bookingStatus,
      } = result;

      // Ignore outdated responses after a location change.
      if (latestLocationRequestRef.current !== requestId) {
        return;
      }
      const statusByDay = new Map();
      bookingStatus.forEach(status => {
        const key = `${status.artist_id}:${moment(status.day).format('YYYY-MM-DD')}`;
        if (!statusByDay.has(key)) statusByDay.set(key, status.bookingStatus);
      });
      const mergedData = availableDays.map(day => ({
        ...day,
        bookingStatus: statusByDay.get(`${day.artistId}:${moment(day.day).format('YYYY-MM-DD')}`) || 'green',
      }));

      setAvailableDays(mergedData);
      setCalendarDataLocationId(requestLocationId);
      setCalendarLoadError('');
      setLoading(false);
      if (scrollToDayId && enableScroll) {
        setTimeout(() => {
          const targetElement = document.querySelector(
            `[data-day-id="${scrollToDayId}"]`
          );

          if (!targetElement) return;

          const offset = targetElement.offsetTop - 100;

          window.scrollTo({
            top: offset,
            behavior: "smooth",
          });

          targetElement.classList.add("highlight-new");

          setTimeout(() => {
            targetElement.classList.remove("highlight-new");
          }, 2000);
        }, 200);
      }
    } catch (error) {
      if (latestLocationRequestRef.current === requestId) {
        setCalendarLoadError('Kalender konnte nicht geladen werden.');
        setLoading(false);
      }
      console.error(
        "Error fetching available days or booking status:",
        error
      );
    }
  }, [selectedLocationId, fetchCalendarData]);


  // aeonas-livesync-phase1-fixed
  // Refresh the visible calendar without changing its navigation state.
  useEffect(() => {
    if (!selectedLocationId) return undefined;
    let inFlight = false;
    const refresh = async () => {
      if (document.visibilityState === 'hidden' || !navigator.onLine || inFlight) return;
      inFlight = true;
      try {
        await fetchAvailableDaysAndBookingStatus(null, false, selectedLocationId);
      } finally {
        inFlight = false;
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const onOnline = () => { void refresh(); };
    const timer = window.setInterval(() => { void refresh(); }, 12000);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [selectedLocationId, fetchAvailableDaysAndBookingStatus]);

  const toggleAppointmentConfirmation = async (appointmentId) => {
    try {
      console.log(`Toggling confirmation for Appointment ID: ${appointmentId}`);


      const response = await toggleConfirmationApi(appointmentId);

      console.log("API Response:", response);

      if (response.success) {
        setGroupedDays((prevGroupedDays) => {
          const updatedDays = { ...prevGroupedDays };

          // ✅ new structure: monthData = { label, artists }
          Object.values(updatedDays).forEach((monthData) => {
            Object.values(monthData.artists || {}).forEach((artist) => {
              (artist.dates || []).forEach((day) => {
                day.appointments = (day.appointments || []).map((a) =>
                  a.id === appointmentId
                    ? { ...a, confirmed: response.confirmed }
                    : a
                );
              });
            });
          });

          console.log("Updated groupedDays after toggle:", updatedDays);
          return updatedDays;
        });
      }
    } catch (error) {
      console.error("Error toggling confirmation:", error.response || error.message);
    }
  };

  const openAddAppointmentModal = (dayId, artistId, day) => {
    prepareNewAppointment(dayId, artistId, day);
    openAddModal();
  };


  const openRetouchAppointmentModal = (appointment, sourceDay) => {
    setRetouchWeekdays([]);
    cancelRescheduleMode();
    closeAppointmentModals();
    setCustomerSearchOpen(false);
    setOpenAppointmentActionsId(null);
    const draft = { appointment: { ...appointment }, sourceDay, previousArtistSearch: retouchDraft?.previousArtistSearch ?? artistSearch };
    if (window.innerWidth <= 768) {
      // Close the modal/keyboard before changing hundreds of calendar nodes.
      document.activeElement?.blur?.();
      setPendingRetouch({ draft, locationId: selectedLocationId });
    } else {
      setRetouchDraft(draft);
      setArtistSearch('');
    }
  };
  const cancelRetouchMode = useCallback(() => {
    setArtistSearch(retouchDraft?.previousArtistSearch || '');
    setRetouchDraft(null);
    closeAppointmentModals();
    resetNewAppointmentData();
  }, [retouchDraft, closeAppointmentModals, resetNewAppointmentData]);
  useEffect(() => {
    if (!retouchDraft) return;
    const handleEscape = event => { if (event.key === 'Escape' && !addModalIsOpen) cancelRetouchMode(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [retouchDraft, addModalIsOpen, cancelRetouchMode]);
  const chooseRetouchDay = (day) => {
    if (!retouchDraft || moment(day.day).isBefore(moment(), 'day')) return;
    const appointment = retouchDraft.appointment;
    prepareNewAppointment(day.dayId, day.artistId, day.day);
    setNewAppointmentData(previous => ({ ...previous,
      appointmentKind: 'retouch', calendarRetouch: true, chooseDay: false, time: '10:00',
      clientName: appointment.clientName || '', phone: appointment.phone || '',
      description: /^Nachstechen\b/i.test(appointment.description || '') ? appointment.description : `Nachstechen${appointment.description ? ` – ${appointment.description}` : ''}`,
      minRange: 0, maxRange: 0, deposit: 0, duration: 60,
      artistName: day.artistName, tattoo_category: appointment.tattoo_category || '',
    }));
    openAddModal();
  };

  const safeScrollTo = useCallback((top, animate = true) => {
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({
      top,
      behavior: animate && !reducedMotion ? "smooth" : "instant",
    });
  }, []);

  useEffect(() => {
    if (!pendingRetouch) return undefined;
    if (String(pendingRetouch.locationId) !== String(selectedLocationId)) {
      setPendingRetouch(null);
      return undefined;
    }
    let commitFrame = null;
    // First let react-modal release its focus trap and scroll lock.
    const scrollFrame = requestAnimationFrame(() => {
      safeScrollTo(0, false);
      commitFrame = requestAnimationFrame(() => {
        setRetouchDraft(pendingRetouch.draft);
        setArtistSearch('');
        setPendingRetouch(null);
      });
    });
    return () => {
      cancelAnimationFrame(scrollFrame);
      if (commitFrame !== null) cancelAnimationFrame(commitFrame);
    };
  }, [pendingRetouch, selectedLocationId, safeScrollTo]);

  const scrollToDay = useCallback((dayId) => {
    pendingMonthNavigationRef.current = null;
    clearTimeout(pendingMonthNavigationTimeoutRef.current);
    const dayElement = document.querySelector(
      `[data-day-id="${dayId}"]`
    );

    if (!dayElement) return;

    const offset = dayElement.getBoundingClientRect().top + window.scrollY - 220;
    safeScrollTo(offset);

    // Keep the highlight while a deferred day card is mounted.
    const highlightElement = dayElement.closest(".calendar-day-shell") || dayElement;
    highlightElement.classList.remove("sidebar-day-highlight");
    void highlightElement.offsetWidth; // Animation neu starten
    highlightElement.classList.add("sidebar-day-highlight");

    setTimeout(() => {
      highlightElement.classList.remove("sidebar-day-highlight");
    }, 1800);
  }, [safeScrollTo]);

  

  

  useEffect(() => {
    if (!appointmentToMove) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        cancelRescheduleMode();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [appointmentToMove, cancelRescheduleMode]);

  useEffect(() => {
    if (!scrollToTarget) return;

    const run = () => {
      const dayId = scrollToTarget;
      const dayEl = document.querySelector(`[data-day-id="${dayId}"]`);

      if (!dayEl) {
        // On mobile the destination month may not be mounted yet.
        const destination = availableDays.find(day => String(day.dayId) === String(dayId));
        if (destination && isMobileCalendar) {
          const destinationMonth = moment(destination.day).format('YYYY-MM');
          if (destinationMonth !== sidebarMonth) {
            pendingMonthNavigationRef.current = destinationMonth;
            setSidebarMonth(destinationMonth);
            setMonthNavigationVersion(version => version + 1);
          }
        }
        highlightTriesRef.current += 1;

        if (highlightTriesRef.current < 20) {
          setTimeout(run, 80);
        } else {
          highlightTriesRef.current = 0;
          setScrollToTarget(null);
        }

        return;
      }

      const pending = pendingHighlightRef.current;

      // ✅ Immer nur zum Tag scrollen
      scrollToDay(dayId);

      // ✅ Tageskarte grün markieren, nur bei neuen manuellen Terminen
      if (pending?.type === "new") {
        dayEl.classList.add("highlight-new");
        setTimeout(() => {
          dayEl.classList.remove("highlight-new");
        }, 2000);
      }

      // ✅ Termin gelb/grün markieren, aber NICHT dahin scrollen
      const idsToHighlight =
        pending?.appointmentIds?.length > 0
          ? pending.appointmentIds
          : pending?.appointmentId
            ? [pending.appointmentId]
            : [];

      if (idsToHighlight.length > 0) {
        const cls =
          pending.type === "edit"
            ? "appointment-highlight-edit"
            : "appointment-highlight-new";

        let tries = 0;

        const tryHighlight = () => {
          const elements = idsToHighlight
            .map((id) =>
              document.querySelector(`[data-appointment-id="${id}"]`)
            )
            .filter(Boolean);

          if (elements.length !== idsToHighlight.length) {
            tries++;

            if (tries < 20) {
              setTimeout(tryHighlight, 80);
            }


            return;
          }

          elements.forEach((el) => {
            el.classList.add(cls);

            setTimeout(() => {
              el.classList.remove(cls);
            }, 3000);
          });
        };

        setTimeout(tryHighlight, 150);
      }


      highlightTriesRef.current = 0;
      setScrollToTarget(null);
    };

    const frame = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => cancelAnimationFrame(frame);
  }, [availableDays, isMobileCalendar, sidebarMonth, highlightTriesRef, pendingHighlightRef, scrollToDay, scrollToTarget, setScrollToTarget]);

  useEffect(() => {
    const targetLocationId = location.state?.targetLocationId;

    if (
      targetLocationId &&
      String(targetLocationId) !== String(selectedLocationId)
    ) {
      setSelectedLocationId(String(targetLocationId));
    }
  }, [
    location.state,
    selectedLocationId,
    setSelectedLocationId,
  ]);




  useEffect(() => {
    const targetLocationId = location.state?.targetLocationId;
    const highlight = location.state?.highlightAppointment;

    if (!highlight || handledNavigationHighlight.current) return;

    // Warten, bis die Daten des Zielstudios vollständig geladen sind.
    if (
      targetLocationId &&
      String(targetLocationId) !== String(selectedLocationId)
    ) return;

    if (calendarBusy || Object.keys(groupedDays).length === 0) return;

    const targetDay = availableDays.find(
      day => String(day.dayId) === String(highlight.dayId)
    );

    if (!targetDay) return;

    const appointment = (targetDay.appointments || []).find(
      item => String(item.id) === String(highlight.appointmentId)
    );

    if (!appointment) return;

    handledNavigationHighlight.current = true;

    // Verhindert, dass die Monatsautomatik den Termin überschreibt.
    didAutoScrollToMonth.current = true;
    setAllowAutoScroll(false);

    setPendingHighlight({
      dayId: highlight.dayId,
      appointmentId: highlight.appointmentId,
      type: "edit",
    });

    setScrollToTarget(highlight.dayId);

    // Termin tatsächlich öffnen, nicht nur markieren.
    openEditModal(appointment, targetDay.dayId, targetDay.day);

    navigate(location.pathname, {
      replace: true,
      state: null,
    });

    setTimeout(() => {
      setAllowAutoScroll(true);
      handledNavigationHighlight.current = false;
    }, 2500);
  }, [
    location.state,
    selectedLocationId,
    calendarBusy,
    groupedDays,
    availableDays,
    navigate,
    location.pathname,
    setPendingHighlight,
    setScrollToTarget
  ]);


  useEffect(() => {
    if (!selectedLocationId) return;

    const locationId = selectedLocationId;

    // Bei jedem Standortwechsel wieder zum aktuellen Monat scrollen
    if (!location.state?.highlightAppointment) {
      didAutoScrollToMonth.current = false;
    }

    setLoading(true);
    setCalendarLoadError('');
    setOpenAppointmentActionsId(null);
    setShowMonthPicker(false);

    const timer = setTimeout(() => {
      fetchAvailableDaysAndBookingStatus(
        null,
        false,
        locationId
      );
    }, 200);

    return () => {
      clearTimeout(timer);

      latestLocationRequestRef.current = null;
    };
  }, [fetchAvailableDaysAndBookingStatus, selectedLocationId]);

  // =======================================================
  // Build Grouped Calendar Data
  // -------------------------------------------------------
  // Transforms available days through useCalendar and stores
  // the result for rendering inside Home.js.
  // =======================================================

  // =======================================================
  // Build Grouped Calendar Data
  // =======================================================

  useEffect(() => {
    const groupedCalendar =
      groupAvailableDays(availableDays);

    const currentMonthKey = moment().format("YYYY-MM");
    if (!groupedCalendar[currentMonthKey]) {
      groupedCalendar[currentMonthKey] = {
        label: moment().format("MMMM YYYY"), artists: {},
      };
    }
    setGroupedDays(groupedCalendar);
    setGroupedLocationId(calendarDataLocationId);
  }, [availableDays, groupAvailableDays, calendarDataLocationId]);


  // =======================================================
  // Auto Scroll To Relevant Month
  // =======================================================

  useEffect(() => {
    if (
      calendarBusy ||
      Object.keys(groupedDays).length === 0
    ) {
      return;
    }

    if (
      !allowAutoScroll ||
      isOperationInProgress
    ) {
      return;
    }

    if (didAutoScrollToMonth.current) {
      return;
    }

    const currentMonthKey = moment().format("YYYY-MM");
    const todayKey = moment().format("YYYY-MM-DD");
    const firstAppointmentTime = day => (day.appointments || [])
      .map(appointment => String(appointment.time || "00:00").slice(0, 5))
      .sort()[0] || "00:00";
    const monthAppointmentDays = availableDays
      .filter(day => moment(day.day).format("YYYY-MM") === currentMonthKey && (day.appointments || []).length > 0)
      .sort((a, b) => moment(a.day).startOf("day").diff(moment(b.day).startOf("day")) ||
        firstAppointmentTime(a).localeCompare(firstAppointmentTime(b)) ||
        String(a.artistName || "").localeCompare(String(b.artistName || "")));
    const landingDay = monthAppointmentDays.find(day => moment(day.day).format("YYYY-MM-DD") === todayKey)
      || monthAppointmentDays[0];
    setSidebarMonth(currentMonthKey);

    let correctionTimer = null;
    let cancelled = false;
    const getTarget = () => landingDay
      ? document.querySelector(`[data-day-id="${landingDay.dayId}"]`)
      : document.getElementById(`month-${currentMonthKey}`);
    const position = target => Math.max(0,
      target.getBoundingClientRect().top + window.scrollY - (landingDay ? 220 : 100));
    const scroll = () => {
      if (cancelled) return;
      const target = getTarget();
      if (!target) return;
      didAutoScrollToMonth.current = true;
      if (landingDay) setActiveSidebarDayId(landingDay.dayId);
      safeScrollTo(position(target), false);
      // A single bounded adjustment after deferred mobile cards have mounted.
      // Never run a scrolling loop; user interaction cancels both movements.
      if (landingDay && window.innerWidth <= 768) correctionTimer = setTimeout(() => {
        if (cancelled) return;
        const mountedTarget = getTarget();
        if (!mountedTarget) return;
        const top = position(mountedTarget);
        if (Math.abs(top - window.scrollY) > 2) safeScrollTo(top, false);
      }, 180);
    };
    const timer = setTimeout(scroll, 150);
    const cancelStartupScroll = () => {
      cancelled = true;
      didAutoScrollToMonth.current = true;
      clearTimeout(timer);
      clearTimeout(correctionTimer);
    };
    const events = ['touchstart', 'pointerdown', 'wheel', 'keydown'];
    events.forEach(type => window.addEventListener(type, cancelStartupScroll, { passive: true }));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearTimeout(correctionTimer);
      events.forEach(type => window.removeEventListener(type, cancelStartupScroll));
    };
  }, [calendarBusy, groupedDays, availableDays, allowAutoScroll, isOperationInProgress, safeScrollTo]);




  useEffect(() => {
    const rebookingAppointment =
      location.state?.rebookingAppointment;

    if (!rebookingAppointment) return;
    if (handledRebookingNavigation.current) return;

    const targetLocationId =
      rebookingAppointment.locationId;

    // Erst auf den Standort des Rebooking-Termins wechseln.
    if (
      targetLocationId &&
      String(targetLocationId) !==
      String(selectedLocationId)
    ) {
      setSelectedLocationId(
        String(targetLocationId)
      );

      return;
    }

    handledRebookingNavigation.current = true;

    // Der vorhandene Umbuchen-Modus bekommt einen
    // künstlichen Termin aus dem Rebooking.
    startRescheduleMode(
      {
        id: null,

        isRebooking: true,
        rebookingId:
          rebookingAppointment.rebookingId,

        clientName:
          rebookingAppointment.clientName || '',

        phone:
          rebookingAppointment.phone || '',

        description:
          rebookingAppointment.description || '',

        time:
          rebookingAppointment.time || '10:00',

        duration:
          rebookingAppointment.duration || 60,

        minRange:
          rebookingAppointment.minRange || 0,

        maxRange:
          rebookingAppointment.maxRange || 0,

        deposit:
          rebookingAppointment.deposit || 0,

        tattoo_category:
          rebookingAppointment.tattoo_category || '',

        originalDay:
          rebookingAppointment.originalDay || null,

        originalArtistName:
          rebookingAppointment.originalArtistName || '',
      },
      null
    );

    // Navigation-State entfernen, damit der Modus nach
    // einem Reload oder Render nicht erneut gestartet wird.
    navigate('/', {
      replace: true,
      state: null,
    });
  }, [
    location.state,
    selectedLocationId,
    setSelectedLocationId,
    startRescheduleMode,
    navigate,
  ]);

  // =======================================================
  // Filter Calendar Data
  // -------------------------------------------------------
  // Filters grouped calendar data through useCalendar.
  // =======================================================

  const filteredGroupedDays = useMemo(
    () => retouchDraft
      ? filterRetouchCalendar(groupedDays, retouchWeekdays, artistSearch)
      : filterGroupedDays(groupedDays, artistSearch, ""),
    [groupedDays, artistSearch, filterGroupedDays, retouchDraft, retouchWeekdays]
  );

  // Open the modal to edit an appointment
  const openEditModal = (appointment, dayId, day) => {
    if (!dayId) {
      console.error("Day ID is missing, cannot open edit modal properly.");
      return;
    }

    const appointmentWithDayId = {
      ...appointment,
      dayId,
    }; prepareAppointmentEdit(appointment, dayId, day);

    // =======================================================
    // Open Edit Appointment Modal
    // -------------------------------------------------------
    // Stores the selected appointment and opens the edit modal
    // through useAppointmentModal.
    // =======================================================

    openEditModalState(appointmentWithDayId);
  };

  // Handle Edit Appointment
  // Handle Edit Appointment
  const handleEditAppointment = async (e) => {
    e.preventDefault();

    if (!selectedAppointment || !selectedAppointment.dayId || !newAppointmentData.time) {
      console.error("Day ID, time, and client name are required");
      return;
    }

    try {
      setIsOperationInProgress(true);
      setIsEditOperation(true);
      setAllowAutoScroll(false); // Deaktiviere AutoScroll!
      // =======================================================
      // Close Edit Modal Before Update
      // -------------------------------------------------------
      // Closes the edit dialog before the update starts.
      // =======================================================

      closeModal();

      const appointmentElement = document.querySelector(
        `[data-appointment-id="${selectedAppointment.id}"]`
      );

      if (appointmentElement) {
        appointmentElement.classList.add("appointment-woom-in");

        setTimeout(() => {
          appointmentElement.classList.remove("appointment-woom-in");
          appointmentElement.classList.add("appointment-woom-out");

          setTimeout(async () => {
            appointmentElement.classList.remove("appointment-woom-out");

            // =======================================================
            // Update Appointment
            // -------------------------------------------------------
            // Updates the appointment through the centralized
            // appointment API hook.
            // =======================================================

            await updateAppointment(selectedAppointment.id, {
              dayId: selectedAppointment.dayId,
              time: newAppointmentData.time,
              clientName: newAppointmentData.clientName,
              phone: newAppointmentData.phone,
              description: newAppointmentData.description,
              project_session_note: newAppointmentData.project_session_note || "",
              minRange: newAppointmentData.minRange,
              maxRange: newAppointmentData.maxRange,
              deposit: newAppointmentData.deposit,
              duration: newAppointmentData.duration,
              tattoo_category: newAppointmentData.tattoo_category,
            });

            console.log("✅ Appointment updated successfully");

            // ✅ REMEMBER WHAT TO HIGHLIGHT AFTER REFRESH
            // =======================================================
            // Queue highlight for the edited appointment.
            // =======================================================

            setPendingHighlight({
              dayId: selectedAppointment.dayId,
              appointmentId: selectedAppointment.id,
              type: "edit",
            });

            // 🔄 Refetch WITHOUT auto-scroll to month
            await fetchAvailableDaysAndBookingStatus(null, false);

            // 🎯 Scroll ONLY to the correct day (not month)
            setTimeout(() => {
              setScrollToTarget(selectedAppointment.dayId);
            }, 200);

            // 🔓 cleanup flags
            setAllowAutoScroll(true);
            setLoading(false);
            setIsEditOperation(false);
            setIsOperationInProgress(false);
          }, 600);
        }, 600);
      } else {
        // Fallback: wenn Element nicht gefunden wird, trotzdem speichern + refresh + scroll
        // =======================================================
        // Update Appointment
        // -------------------------------------------------------
        // Fallback update if the appointment animation element
        // is not available in the DOM.
        // =======================================================

        await updateAppointment(selectedAppointment.id, {
          dayId: selectedAppointment.dayId,
          time: newAppointmentData.time,
          clientName: newAppointmentData.clientName,
          phone: newAppointmentData.phone,
          description: newAppointmentData.description,
              project_session_note: newAppointmentData.project_session_note || "",
          minRange: newAppointmentData.minRange,
          maxRange: newAppointmentData.maxRange,
          deposit: newAppointmentData.deposit,
          duration: newAppointmentData.duration,
          tattoo_category: newAppointmentData.tattoo_category,
        });

        // =======================================================
        // Queue Highlight for Edited Appointment
        // -------------------------------------------------------
        // Stores the edited appointment as the next highlight
        // target after the calendar refresh.
        // =======================================================

        setPendingHighlight({
          dayId: selectedAppointment.dayId,
          appointmentId: selectedAppointment.id,
          type: "edit",
        });
        await fetchAvailableDaysAndBookingStatus(null, false);

        setTimeout(() => {
          setScrollToTarget(selectedAppointment.dayId);
        }, 200);

        setAllowAutoScroll(true);
        setLoading(false);
        setIsEditOperation(false);
        setIsOperationInProgress(false);
      }
    } catch (err) {
      console.error(
        "Error updating appointment:",
        err.response ? err.response.data : err.message
      );
      setAllowAutoScroll(true);
      setIsEditOperation(false);
      setIsOperationInProgress(false);
    }
  }

  // =======================================================
  // Close Appointment Modals
  // -------------------------------------------------------
  // Closes appointment modals and resets appointment form.
  // =======================================================

  const closeModal = () => {
    closeAppointmentModals();
    resetNewAppointmentData();
  };

  // =======================================================
  // Handle Reschedule Appointment
  // -------------------------------------------------------
  // Validates the selected appointment, target day and time,
  // performs the reschedule request and refreshes the calendar.
  // =======================================================

  const handleRescheduleAppointment = async (e) => {
    e.preventDefault();

    if (!appointmentToMove || !targetDay || !rescheduleTime) {
      console.error("Missing appointment, target day or time");
      return;
    }

    try {
      setIsOperationInProgress(true);
      setAllowAutoScroll(false);

      let response;

      if (appointmentToMove.isRebooking) {
        const result = await axios.post(
          `/api/rebooking/${appointmentToMove.rebookingId}/book`,
          {
            dayId: targetDay.dayId,
            time: rescheduleTime,
          },
          {
            withCredentials: true,
          }
        );

        response = result.data;
      } else {
        response = await rescheduleAppointment(
          appointmentToMove.id,
          targetDay.dayId,
          rescheduleTime
        );
      }

      cancelRescheduleMode();

      await fetchAvailableDaysAndBookingStatus(
        null,
        false
      );

      const appointmentId =
        response?.appointmentId ||
        response?.id ||
        appointmentToMove.id;

      setPendingHighlight({
        dayId: targetDay.dayId,
        appointmentId,
        type: "new",
      });

      setTimeout(() => {
        setScrollToTarget(targetDay.dayId);
      }, 200);
    } catch (error) {
      console.error(
        "Error rescheduling appointment:",
        error.response?.data || error.message
      );

      alert(
        error.response?.data?.message ||
        "Der Termin konnte nicht vergeben werden."
      );
    } finally {
      setAllowAutoScroll(true);
      setIsOperationInProgress(false);
    }
  };

  const handleMoveToRebooking = async () => {
    if (!appointmentToMove?.id) return;

    const confirmed = window.confirm(
      `${appointmentToMove.clientName || 'Diesen Termin'} wirklich ins Rebooking verschieben?`
    );

    if (!confirmed) return;

    try {
      await axios.post(
        `/api/rebooking/from-appointment/${appointmentToMove.id}`,
        {},
        {
          withCredentials: true,
        }
      );

      cancelRescheduleMode();

      await fetchAvailableDaysAndBookingStatus();

      navigate('/rebooking');
    } catch (error) {
      console.error(
        'Fehler beim Verschieben ins Rebooking:',
        error.response?.data || error.message
      );

      alert(
        error.response?.data?.message ||
        'Der Termin konnte nicht ins Rebooking verschoben werden.'
      );
    }
  };



  // Handle Add Appointment
  const handleAddAppointment = async e => {
    e.preventDefault();
    const {
      dayId,
      artistId,
      time,
      clientName,
      phone,
      description,
      minRange,
      maxRange,
      deposit,
      duration,
      tattoo_category,
      appointmentKind
    } = newAppointmentData;
    if (!dayId || !artistId || !time || !clientName) {
      alert("Bitte Tag, Kunde und Uhrzeit eintragen.");
      return;
    }
    if (newAppointmentData.isSaving) return;
    const isRetouchBooking = appointmentKind === "retouch";
    const parsePrice = value => {
      const raw = String(value ?? "").trim().replace(/\s/g, "").replace(",", ".");
      if (!raw || !/^(?:\d+)(?:\.\d{1,2})?$/.test(raw)) return null;
      const number = Number(raw);
      return Number.isFinite(number) ? number : null;
    };
    const minimumPrice = isRetouchBooking ? 0 : parsePrice(minRange);
    const maximumPrice = isRetouchBooking ? 0 : parsePrice(maxRange);
    if (!isRetouchBooking && (minimumPrice === null || maximumPrice === null || minimumPrice <= 0 || maximumPrice <= 0)) {
      alert("Preisangabe fehlt oder ist ungültig. Bitte Mindestpreis und Höchstpreis größer als 0 € eintragen.");
      return;
    }
    if (!isRetouchBooking && maximumPrice < minimumPrice) {
      alert("Der Höchstpreis darf nicht unter dem Mindestpreis liegen.");
      return;
    }
    const phoneRaw = (phone || "").trim();
    const digits = phoneRaw.replace(/\D/g, "");

    // if user entered something AND it's too short -> stop and keep modal open
    if (phoneRaw && digits.length < 6) {
      alert("Telefonnummer ist ungültig. Bitte richtig eingeben (z.B. 0176..., +49..., etc.).");
      return;
    }
    try {
      setIsOperationInProgress(true);
      setNewAppointmentData(previousData => ({
        ...previousData,
        isSaving: true
      }));
      setAllowAutoScroll(false); // ⛔ AutoScroll verhindern!
      const safeDeposit = deposit !== undefined && deposit !== '' ? deposit : 0;
      const isRetouch = appointmentKind === "retouch";
      const savedDescription = isRetouch && !/^Nachstechen\b/i.test(description || "") ? `Nachstechen${description ? ` – ${description}` : ""}` : description;

      // =======================================================
      // Create Appointment
      // -------------------------------------------------------
      // Creates the appointment through the centralized
      // appointment API hook.
      // =======================================================

      const response = await createAppointment({
        dayId,
        time,
        clientName,
        phone,
        description: savedDescription,
        minRange: minimumPrice,
        maxRange: maximumPrice,
        appointmentKind: isRetouch ? "retouch" : "tattoo",
        deposit: isRetouch ? 0 : safeDeposit,
        duration,
        tattoo_category
      });

      if (!Number.isSafeInteger(Number(response?.id)) || Number(response.id) <= 0) {
        throw new Error("Der Server hat keine gültige Termin-ID bestätigt. Bitte vor einem erneuten Versuch prüfen, ob der Termin angelegt wurde.");
      }

      // =======================================================
      // Queue Highlight for New Appointment
      // -------------------------------------------------------
      // Stores the newly created appointment as the next
      // highlight target after the calendar refresh.
      // =======================================================

      setPendingHighlight({
        dayId,
        appointmentId: response?.id,
        type: "new"
      });

      // refetch without scroll
      await fetchAvailableDaysAndBookingStatus(null, false);

      // now trigger scroll-to-day (keeps your old behavior)
      setTimeout(() => setScrollToTarget(dayId), 200);
      closeAppointmentModals();
      resetNewAppointmentData();
      if (newAppointmentData.calendarRetouch) setRetouchDraft(null);
      // Keep startup auto-scroll disabled while the saved day is being located.
      didAutoScrollToMonth.current = true;
      setAllowAutoScroll(false);
      setLoading(false);
      setIsOperationInProgress(false);
    } catch (err) {
      console.error("Error adding appointment:", err.message);
      setNewAppointmentData(previousData => ({
        ...previousData,
        isSaving: false
      }));
      alert(err.response?.data?.message || err.message || "Termin konnte nicht gespeichert werden. Bitte erneut versuchen.");
      setAllowAutoScroll(true);
      setIsOperationInProgress(false);
    }
  };
  const handleDeleteAppointment = async (appointmentId, dayId) => {
    if (!appointmentId || !dayId) {
      console.error("Invalid appointment ID or day ID");
      return;
    }

    if (window.confirm("Are you sure you want to delete this appointment?")) {
      try {
        setIsOperationInProgress(true); // Mark operation as in progress
        // Close the modal immediately
        closeModal();
        // Find the appointment DOM element
        const appointmentElement = document.querySelector(`[data-appointment-id="${appointmentId}"]`);
        if (appointmentElement) {
          // Apply the fade-out animation
          appointmentElement.classList.add("appointment-fade-out");

          // Wait for the animation to finish before removing it
          setTimeout(async () => {
            // =======================================================
            // Delete Appointment
            // -------------------------------------------------------
            // Deletes the appointment through the centralized
            // appointment API hook.
            // =======================================================

            await deleteAppointment(appointmentId);

            // Fetch fresh data and booking status
            await fetchAvailableDaysAndBookingStatus();

            // Scroll to the corresponding day element
            scrollToDay(dayId);
            setAllowAutoScroll(true);
            setIsOperationInProgress(false);
          }, 1500); // Match the animation duration (1.5 seconds)
        } else {
          // If the DOM element is not found, directly proceed with deletion and data refresh
          // =======================================================
          // Delete Appointment
          // -------------------------------------------------------
          // Deletes the appointment through the centralized
          // appointment API hook.
          // =======================================================

          await deleteAppointment(appointmentId);

          // Fetch fresh data and booking status
          await fetchAvailableDaysAndBookingStatus();

          // Scroll to the corresponding day element
          scrollToDay(dayId);
          // =======================================================
          // Reset Delete Operation State
          // -------------------------------------------------------
          // Restores scrolling and operation state after deletion.
          // =======================================================

          setAllowAutoScroll(true);
          setIsOperationInProgress(false);
        }
      } catch (error) {
        console.error("Error deleting appointment:", error.response?.data || error.message);
      }
    }
  };


  // Helper function to scroll to a specific day
  

  const availableMonthKeys = useMemo(
    () => Object.keys(filteredGroupedDays).sort(),
    [filteredGroupedDays]
  );

  const mobileRenderedMonthKeys = useMemo(() => {
    if (!isMobileCalendar) return availableMonthKeys;
    const monthIndex = Math.max(0, availableMonthKeys.indexOf(sidebarMonth));
    return availableMonthKeys.slice(
      Math.max(0, monthIndex - 1),
      monthIndex + 2
    );
  }, [availableMonthKeys, isMobileCalendar, sidebarMonth]);

  const monthEntriesToRender = useMemo(() => {
    const keys = isMobileCalendar
      ? mobileRenderedMonthKeys
      : availableMonthKeys;
    return keys
      .map(monthKey => [monthKey, filteredGroupedDays[monthKey]])
      .filter(([, monthData]) => Boolean(monthData));
  }, [
    availableMonthKeys,
    filteredGroupedDays,
    isMobileCalendar,
    mobileRenderedMonthKeys
  ]);

  const scrollToMonth = useCallback((monthKey) => {
    if (!monthKey) return;

    sidebarDayNavigationRef.current += 1;
    setPendingSidebarDay(null);

    pendingMonthNavigationRef.current = monthKey;
    clearTimeout(pendingMonthNavigationTimeoutRef.current);
    pendingMonthNavigationTimeoutRef.current = window.setTimeout(() => {
      if (pendingMonthNavigationRef.current === monthKey) {
        pendingMonthNavigationRef.current = null;
      }
    }, 5000);

    setSidebarMonth(monthKey);
    setShowMonthPicker(false);
    setMonthNavigationVersion(version => version + 1);
  }, []);

  // Day selection must mount its own month before attempting to scroll.
  const selectSidebarDay = useCallback((dayId) => {
    const target = availableDays.find(day => String(day.dayId) === String(dayId));
    if (!target) return;
    const monthKey = moment(target.day).format('YYYY-MM');
    if (!availableMonthKeys.includes(monthKey)) return;

    const version = ++sidebarDayNavigationRef.current;
    clearTimeout(pendingMonthNavigationTimeoutRef.current);
    pendingMonthNavigationRef.current = monthKey;
    setAllowAutoScroll(false);
    didAutoScrollToMonth.current = true;
    setActiveSidebarDayId(dayId);
    setSidebarMonth(monthKey);
    setShowMonthPicker(false);
    setPendingSidebarDay({ dayId, monthKey, version });
  }, [availableDays, availableMonthKeys]);

  useEffect(() => {
    if (!pendingSidebarDay) return undefined;
    const { dayId, monthKey, version } = pendingSidebarDay;
    let timer;
    let cancelled = false;
    let attempts = 0;

    const locate = () => {
      if (cancelled || sidebarDayNavigationRef.current !== version) return;
      const month = document.getElementById(`month-${monthKey}`);
      const day = month?.querySelector(`[data-day-id="${dayId}"]`);
      if (!day) {
        if (++attempts < 35) timer = window.setTimeout(locate, 60);
        else {
          pendingMonthNavigationRef.current = null;
          setPendingSidebarDay(null);
        }
        return;
      }
      const position = Math.max(0, day.getBoundingClientRect().top + window.scrollY - 220);
      safeScrollTo(position, false);
      // Deferred day cards can change height after first paint.
      timer = window.setTimeout(() => {
        if (cancelled || sidebarDayNavigationRef.current !== version) return;
        const updated = document.getElementById(`month-${monthKey}`)
          ?.querySelector(`[data-day-id="${dayId}"]`);
        if (updated) {
          const corrected = Math.max(0, updated.getBoundingClientRect().top + window.scrollY - 220);
          if (Math.abs(corrected - window.scrollY) > 3) safeScrollTo(corrected, false);
          const highlight = updated.closest('.calendar-day-shell') || updated;
          highlight.classList.remove('sidebar-day-highlight');
          void highlight.offsetWidth;
          highlight.classList.add('sidebar-day-highlight');
          window.setTimeout(() => highlight.classList.remove('sidebar-day-highlight'), 1800);
        }
        pendingMonthNavigationRef.current = null;
        setPendingSidebarDay(null);
      }, 220);
    };
    timer = window.setTimeout(locate, 0);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [pendingSidebarDay, monthEntriesToRender, safeScrollTo]);

  const changeSidebarMonth = useCallback((direction) => {
    const currentIndex = availableMonthKeys.indexOf(sidebarMonth);
    let targetIndex;

    if (currentIndex >= 0) {
      targetIndex = currentIndex + direction;
    } else if (direction > 0) {
      targetIndex = availableMonthKeys.findIndex(monthKey => monthKey > sidebarMonth);
    } else {
      targetIndex = -1;
      for (let index = availableMonthKeys.length - 1; index >= 0; index -= 1) {
        if (availableMonthKeys[index] < sidebarMonth) {
          targetIndex = index;
          break;
        }
      }
    }

    const nextMonth = availableMonthKeys[targetIndex];
    if (nextMonth) scrollToMonth(nextMonth);
  }, [availableMonthKeys, sidebarMonth, scrollToMonth]);

  useEffect(() => {
    const monthKey = pendingMonthNavigationRef.current;
    if (!monthKey) return;

    const target = document.getElementById(`month-${monthKey}`);
    if (!target) return;

    const top = target.getBoundingClientRect().top + window.scrollY - 145;
    safeScrollTo(top, false);

    // Keep navigation locked until the scroll observer confirms the target is visible.
  }, [monthEntriesToRender, monthNavigationVersion, safeScrollTo]);

  useEffect(() => {
    const handleMonthShortcut = (event) => {
      const activeElement = document.activeElement;
      const tagName = activeElement?.tagName;

      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        tagName === "INPUT" ||
        tagName === "TEXTAREA" ||
        tagName === "SELECT" ||
        activeElement?.isContentEditable
      ) {
        return;
      }

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        changeSidebarMonth(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        changeSidebarMonth(1);
      }
    };

    window.addEventListener("keydown", handleMonthShortcut);

    return () => {
      window.removeEventListener("keydown", handleMonthShortcut);
    };
  }, [sidebarMonth, availableMonthKeys, changeSidebarMonth]);

  useEffect(() => {
    if (!availableMonthKeys.length) return;

    let animationFrameId = null;

    const updateActiveSidebarMonth = () => {
      animationFrameId = null;

      // Profile navigation follows the visible month on every screen size.
      const detectionLine = 170;
      if (pendingSidebarDay) return;
      const pendingMonth = pendingMonthNavigationRef.current;
      if (pendingMonth) {
        const targetMonth = document.getElementById(`month-${pendingMonth}`);
        if (!targetMonth) return;

        const targetRect = targetMonth.getBoundingClientRect();
        if (targetRect.top > detectionLine || targetRect.bottom <= detectionLine) {
          return;
        }

        pendingMonthNavigationRef.current = null;
        clearTimeout(pendingMonthNavigationTimeoutRef.current);
      }


      let detectedMonth = null;

      for (const monthKey of availableMonthKeys) {
        const monthElement = document.getElementById(
          `month-${monthKey}`
        );

        if (!monthElement) continue;

        const rect =
          monthElement.getBoundingClientRect();

        // Der Monat, dessen Bereich auf Höhe der Sidebar liegt.
        if (
          rect.top <= detectionLine &&
          rect.bottom > detectionLine
        ) {
          detectedMonth = monthKey;
          break;
        }
      }

      // Am Seitenanfang oder in einem Zwischenraum:
      // den nächsten sichtbaren Monat verwenden.
      if (!detectedMonth) {
        const visibleMonths = availableMonthKeys
          .map((monthKey) => {
            const element = document.getElementById(
              `month-${monthKey}`
            );

            if (!element) return null;

            const rect = element.getBoundingClientRect();

            return {
              monthKey,
              distance: Math.abs(rect.top - detectionLine),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.distance - b.distance);

        detectedMonth =
          visibleMonths[0]?.monthKey || null;
      }

      if (!detectedMonth) return;

      setSidebarMonth((currentMonth) =>
        currentMonth === detectedMonth
          ? currentMonth
          : detectedMonth
      );
    };

    const handleScroll = () => {
      if (animationFrameId !== null) return;

      animationFrameId = requestAnimationFrame(
        updateActiveSidebarMonth
      );
    };

    updateActiveSidebarMonth();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    window.addEventListener("resize", handleScroll);

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );

      window.removeEventListener(
        "resize",
        handleScroll
      );

      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [availableMonthKeys, pendingSidebarDay]);

  useEffect(() => {
    let frame = null;

    const detectActiveDay = () => {
      frame = null;

      if (window.innerWidth > 768) return;

      const dayElements = Array.from(
        document.querySelectorAll("[data-day-id]")
      );

      if (!dayElements.length) return;

      // Linie ungefähr unterhalb des sticky Monats-Headers.
      const detectionLine = 300;

      let bestElement = null;
      let bestDistance = Infinity;

      dayElements.forEach((element) => {
        const rect = element.getBoundingClientRect();

        // Nur Elemente berücksichtigen, die zumindest
        // teilweise im sichtbaren Bereich liegen.
        if (
          rect.bottom < 160 ||
          rect.top > window.innerHeight
        ) {
          return;
        }

        const distance = Math.abs(
          rect.top - detectionLine
        );

        if (distance < bestDistance) {
          bestDistance = distance;
          bestElement = element;
        }
      });

      if (!bestElement) return;

      const dayId =
        bestElement.getAttribute("data-day-id");

      if (!dayId) return;

      setActiveSidebarDayId((current) =>
        String(current) === String(dayId)
          ? current
          : dayId
      );
    };

    const handleScroll = () => {
      if (frame !== null) return;

      frame = requestAnimationFrame(
        detectActiveDay
      );
    };

    detectActiveDay();

    window.addEventListener(
      "scroll",
      handleScroll,
      { passive: true }
    );

    window.addEventListener(
      "resize",
      handleScroll
    );

    return () => {
      window.removeEventListener(
        "scroll",
        handleScroll
      );

      window.removeEventListener(
        "resize",
        handleScroll
      );

      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
    };
  }, [filteredGroupedDays]);

  const getSidebarStatusClass = (status) => {
    if (status === 'red') return 'red';
    if (status === 'yellow') return 'yellow';
    return 'green';
  };

  // =======================================================
  // Build Mobile Sidebar
  // -------------------------------------------------------
  // Creates the mobile sidebar data through useCalendar.
  // =======================================================

  const mobileSidebar = useMemo(() => {
    const sidebar = buildMobileSidebar(
      filteredGroupedDays,
      sidebarMonth
    );

    if (
      !activeSidebarArtist ||
      !sidebar[activeSidebarArtist]
    ) {
      return sidebar;
    }

    return {
      [activeSidebarArtist]:
        sidebar[activeSidebarArtist],

      ...Object.fromEntries(
        Object.entries(sidebar).filter(
          ([artistName]) =>
            artistName !==
            activeSidebarArtist
        )
      ),
    };
  }, [
    filteredGroupedDays,
    sidebarMonth,
    activeSidebarArtist,
    buildMobileSidebar,
  ]);
  return (

    <div className={`home-container ${retouchDraft ? 'retouch-calendar-active' : ''}`}>
      {projectAppointment && <TattooProjectDialog
        appointment={projectAppointment} onClose={() => setProjectAppointment(null)}
        locationId={selectedLocationId} locations={locations} onShare={shareImage}
        onChanged={() => fetchAvailableDaysAndBookingStatus(null, false)} />}


      {/* Location dropdown */}
      {/* =======================================================
    Calendar Toolbar
    -------------------------------------------------------
    Delegates month navigation and search controls.
======================================================= */}

      <CalendarToolbar
        filteredGroupedDays={filteredGroupedDays}
        artistSearch={artistSearch}
        onArtistSearchChange={setArtistSearch}
        onOpenCustomerSearch={() => { setMobileCustomerQuery(''); setCustomerSearchOpen(true); }}
        onScrollToMonth={scrollToMonth}
        onScrollToTop={() => safeScrollTo(0)}
      />
      <BookingDiagnostics renderCount={bookingRenderCount} days={availableDays} loading={loading} locationId={selectedLocationId} />
      <CustomerSearchDialog initialQuery={mobileCustomerQuery} isOpen={customerSearchOpen} onClose={() => setCustomerSearchOpen(false)} availableDays={availableDays} loading={loading} locationName={locations.find(item => Number(item.id) === Number(selectedLocationId))?.name} selectedLocationId={selectedLocationId} onEdit={(appointment, dayId, day, locationId) => {
          const targetLocationId = locationId || selectedLocationId;
          if (String(targetLocationId) !== String(selectedLocationId)) {
            setCustomerSearchOpen(false);
            navigate('/', {
              state: {
                targetLocationId,
                highlightAppointment: {
                  appointmentId: appointment.id,
                  dayId
                }
              }
            });
          } else {
            openEditModal(appointment, dayId, day);
          }
        }} onScheduleRetouch={openRetouchAppointmentModal} />
      <CalendarLoadingState busy={calendarBusy} error={calendarLoadError}
        locationName={locations.find(item => Number(item.id) === Number(selectedLocationId))?.name}
        onRetry={() => { setCalendarLoadError(''); setLoading(true); fetchAvailableDaysAndBookingStatus(null, false); }} />
      <MobileMonthNavigation
        monthKeys={availableMonthKeys}
        activeMonth={sidebarMonth}
        onSelectMonth={scrollToMonth}
      />
      <div className={`mobile-layout ${calendarBusy ? 'calendar-content-loading' : ''}`}
        aria-busy={calendarBusy} inert={calendarBusy ? '' : undefined}>

        {/* =======================================================
    Mobile Calendar Sidebar
    -------------------------------------------------------
    Delegates mobile month and day navigation.
======================================================= */}

        <MobileCalendarSidebar
          sidebarMonth={sidebarMonth}
          showMonthPicker={showMonthPicker}
          availableMonthKeys={availableMonthKeys}
          mobileSidebar={mobileSidebar}
          artistProfiles={artistProfiles}
          activeDayId={activeSidebarDayId}
          onChangeMonth={changeSidebarMonth}
          onToggleMonthPicker={() =>
            setShowMonthPicker((previousValue) => !previousValue)
          }
          onSelectMonth={scrollToMonth}
          onScrollToDay={selectSidebarDay} getStatusClass={getSidebarStatusClass}
        />

        <main className="mobile-content">
          {calendarBusy && availableDays.length === 0 && <div className="calendar-initial-skeleton" aria-hidden="true">
            {[1, 2, 3].map(index => <div className="calendar-skeleton-card" key={index}><i /><i /><i /></div>)}
          </div>}

          {/* Display Available Days – optimiert */}
          {/* =======================================================
    Calendar Month Sections
    -------------------------------------------------------
    Delegates month rendering to MonthSection.
======================================================= */}

          {retouchDraft && Object.keys(filteredGroupedDays).length === 0 && (
            <div className="retouch-calendar-empty" role="status">
              <p>Für diese Auswahl sind keine kommenden Künstler-Tage hinterlegt.</p>
              {retouchWeekdays.length > 0 && <button type="button" onClick={() => setRetouchWeekdays([])}>Alle Tage anzeigen</button>}
            </div>
          )}
          {monthEntriesToRender.map(([monthKey, monthData]) => (
              <MonthSection
                key={`month-${monthKey}`}
                monthKey={monthKey}
                monthData={monthData}
                viewMode={viewMode}
                onArtistSelect={(artistName) => {
                  setSidebarMonth(monthKey);
                  setActiveSidebarArtist(artistName);
                }}
                renderDay={(day, artistInfo) => (
                  <DayCard
                    key={day.dayId}
                    day={day}
                    artistInfo={artistInfo}
                    viewMode={viewMode}
                    holidaysNRW={holidaysNRW}
                    rescheduleMode={rescheduleMode}
                    retouchMode={Boolean(retouchDraft)}
                    onPickRetouchDay={chooseRetouchDay}
                    appointmentToMove={appointmentToMove}
                    todayDayRef={todayDayRef}
                    onOpenAddAppointment={openAddAppointmentModal}
                    onOpenRescheduleModal={openRescheduleModal}
                    renderAppointment={(appointment, appointmentDay) => (
                      <AppointmentCard
                        key={appointment.id}
                        appointment={appointment}
                        day={appointmentDay}
                        locations={locations}
                        selectedLocationId={selectedLocationId}
                        groupedDays={groupedDays}
                        openAppointmentActionsId={openAppointmentActionsId}
                        appointmentToMove={appointmentToMove}
                        rescheduleMode={rescheduleMode}
                        onToggleActions={(appointmentId) => {
                          setOpenAppointmentActionsId((previousId) =>
                            previousId === appointmentId ? null : appointmentId
                          );
                        }}
                        onEdit={openEditModal}
                        onOpenImages={(images, imageIndex) => {
                          setViewerImages(images);
                          setCurrentImage(imageIndex);
                        }}
                        onToggleConfirmation={toggleAppointmentConfirmation}
                        onStartReschedule={(...args) => { setRetouchDraft(null); startRescheduleMode(...args); }}
                        onOpenProject={openProject}
                        onScheduleRetouch={openRetouchAppointmentModal}
                      />
                    )}
                  />
                )}
              />
            ))}

        </main>
      </div>

      {/* Transparent Modal for adding a new appointment */}
      {/* =======================================================
    Add Appointment Modal
    -------------------------------------------------------
    Delegates appointment creation form rendering.
======================================================= */}

      <AddAppointmentModal
        locationId={selectedLocationId}
        availableDays={availableDays}
        isOpen={addModalIsOpen}
        onClose={closeModal}
        onSubmit={handleAddAppointment}
        appointmentData={newAppointmentData}
        setAppointmentData={setNewAppointmentData}
        copiedAppointment={copiedAppointment}
        setCopiedAppointment={setCopiedAppointment}
        categories={TATTOO_CATEGORIES}
        onOpenImages={(images, imageIndex) => {
          setViewerImages(images);
          setCurrentImage(imageIndex);
        }}
        onImagesChanged={() => {
          fetchAvailableDaysAndBookingStatus(
            null,
            false
          );
        }}
      />

      {/* Transparent Modal for editing an appointment */}
      {/* =======================================================
    Edit Appointment Modal
    -------------------------------------------------------
    Delegates appointment editing form rendering.
======================================================= */}

      <EditAppointmentModal
        isOpen={editModalIsOpen}
        onClose={closeModal}
        onSubmit={handleEditAppointment}
        appointmentData={newAppointmentData}
        setAppointmentData={setNewAppointmentData}
        selectedAppointment={selectedAppointment}
        onOpenProject={openProject}
        onOpenImages={(images, index) => { setViewerImages(images); setCurrentImage(index); }}
        onImagesChanged={() => fetchAvailableDaysAndBookingStatus(null, false)}
        onCopyAppointment={(appointment) => {
          setCopiedAppointment(appointment);
          alert(
            "Termin kopiert! Du kannst ihn jetzt in einen neuen Termin einfügen."
          );
          closeModal();
        }}
        onDeleteAppointment={handleDeleteAppointment}
        isDeleteHover={isDeleteHover}
        setIsDeleteHover={setIsDeleteHover}
        categories={TATTOO_CATEGORIES}
      />

      {retouchDraft && !addModalIsOpen && <RetouchCalendarOptions
        clientName={retouchDraft.appointment.clientName} weekdays={retouchWeekdays}
        onWeekdaysChange={setRetouchWeekdays} onCancel={cancelRetouchMode} /> }
      {appointmentToMove && !rescheduleModalIsOpen && (
        <div className="reschedule-command-bar">
          <div className="reschedule-command-main">
            <span className="reschedule-command-badge">UMBUCHEN</span>

            <span className="reschedule-inline-client">
              {appointmentToMove.clientName}
            </span>

            {appointmentToMove.originalDay && appointmentToMove.time && (
              <span className="reschedule-inline-chip">
                {moment(appointmentToMove.originalDay).format("DD.MM.YYYY")} ·{" "}
                {moment(appointmentToMove.time, "HH:mm:ss").format("HH:mm")} Uhr
              </span>
            )}

            {appointmentToMove.originalArtistName && (
              <span className="reschedule-inline-chip">
                {appointmentToMove.originalArtistName}
              </span>
            )}

            {appointmentToMove.description && (
              <span className="reschedule-inline-chip reschedule-inline-description">
                {appointmentToMove.description}
              </span>
            )}
          </div>

          <div className="reschedule-command-actions">
            {!appointmentToMove.isRebooking && (
              <button
                type="button"
                className="reschedule-command-rebooking"
                onClick={handleMoveToRebooking}
              >
                Ins Rebooking
              </button>
            )}

            <button
              type="button"
              className="reschedule-command-cancel"
              onClick={cancelRescheduleMode}
            >
              Abbrechen · ESC
            </button>
          </div>
        </div>
      )}

      {/* =======================================================
    Reschedule Modal
    -------------------------------------------------------
    Delegates appointment rescheduling form rendering.
======================================================= */}

      <RescheduleModal
        isOpen={rescheduleModalIsOpen}
        appointment={appointmentToMove}
        targetDay={targetDay}
        rescheduleTime={rescheduleTime}
        setRescheduleTime={setRescheduleTime}
        onSubmit={handleRescheduleAppointment}
        onCancel={cancelRescheduleMode}
      />
      {/* =======================================================
    Appointment Image Viewer
    -------------------------------------------------------
    Displays reference images through a dedicated component.
======================================================= */}
      <AppointmentImageViewer
        images={viewerImages}
        currentImage={currentImage}
        setCurrentImage={setCurrentImage}
        onClose={() => {
          setViewerImages([]);
          setCurrentImage(0);
        }}
        onShare={shareImage}
      />

    </div>
  );
};

export default Home;